import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, AlertTriangle, Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 41 (2026-07-28) — Admin review queue for auto-heal escalations.
 * Non-blocking to normal operations. Empty when auto-heal ran clean.
 */

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminReviewQueuePage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/auto-heal/review-queue?status=${status}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load review queue");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const resolve = async (id, newStatus) => {
    const note = window.prompt(`Optional note for ${newStatus}:`, "") || "";
    try {
      await api.patch(`/admin/auto-heal/review-queue/${id}`,
        { status: newStatus, resolution_note: note });
      toast.success(`Marked ${newStatus}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="admin-review-queue-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Inbox className="h-6 w-6 text-amber-600" /> Admin Review Queue
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Data-integrity findings the Auto-Heal engine couldn&apos;t resolve safely on its own.
          Nothing here is blocking — everything is optional cleanup.
          {" "}
          <Link to="/admin/auto-heal-log" className="text-blue-600 hover:underline"
                 data-testid="link-back-to-log">
            View auto-heal log →
          </Link>
        </p>
      </div>

      <div className="flex gap-2" data-testid="status-tabs">
        {["pending", "resolved", "dismissed", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
              status === s ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`tab-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center"
             data-testid="empty-state">
          <p className="text-slate-500">
            {status === "pending" ? "🎉 No pending items — auto-heal is on top of things." : `No ${status} items.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                  data-testid={`review-item-${it.id}`}>
              <div className="flex items-start gap-3 p-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
                      {it.rule_id}
                    </span>
                    <span className="text-[10px] text-slate-400">{fmtTime(it.created_at)}</span>
                    {it.status !== "pending" && (
                      <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${
                        it.status === "resolved" ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-100 text-slate-600"
                      }`}>{it.status}</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-800 font-medium">{it.subject}</p>
                  {it.resolution_note && (
                    <p className="mt-1 text-xs text-slate-500 italic">Note: {it.resolution_note}</p>
                  )}
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [it.id]: !s[it.id] }))}
                    className="mt-1 text-xs text-blue-600 hover:underline"
                    data-testid={`expand-${it.id}`}
                  >
                    {expanded[it.id] ? "Hide details" : "Show details"}
                  </button>
                  {expanded[it.id] && (
                    <pre className="mt-2 text-[10px] bg-slate-50 p-2 rounded border overflow-x-auto max-w-full">{JSON.stringify(it.details, null, 2)}</pre>
                  )}
                </div>
                {it.status === "pending" && (
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => resolve(it.id, "resolved")}
                      className="rounded-md bg-emerald-600 text-white text-xs px-2 py-1 hover:bg-emerald-700 inline-flex items-center gap-1"
                      data-testid={`resolve-${it.id}`}
                    >
                      <Check className="h-3 w-3" /> Resolve
                    </button>
                    <button
                      onClick={() => resolve(it.id, "dismissed")}
                      className="rounded-md bg-slate-200 text-slate-700 text-xs px-2 py-1 hover:bg-slate-300 inline-flex items-center gap-1"
                      data-testid={`dismiss-${it.id}`}
                    >
                      <X className="h-3 w-3" /> Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
